import { AssessmentInfo, Patient, User, FileResponse, TeethType } from '@opp/shared';
import { AssessmentContext } from './AssessmentContext';
import { useEffect, useState, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { EditImage } from '@/components/EditImage/EditImage';
import { PreviewImage } from '@/components/PreviewImage';
import { useAuth } from '@/hooks/useAuth';
import { useCreateAssessment } from '@/hooks/useAssessmentMutations';
import { CustomError } from '@/utils/CustomError';
import { getErrorMessage } from '@/utils/getErrorMessage';  
import { getAssessment, updateAssessmentFiles, updateAssessment as updateAssessmentService } from '@/services/assessmentService';
import { uploadFile } from '@/services/uploadService';
import { deleteFiles } from '@/services/commonService';
import { useToast } from '@/components/Toast';
import teethData from "@/data/teeth.json";
import { LocalTeethType } from "@/types/app";

/** Snapshot do que `updateAssessment` persiste — evita toast ao abrir o atendimento (Strict Mode dispara o efeito 2x). */
function autoPersistSnapshot(info: AssessmentInfo | undefined, teethState: TeethType[] | undefined): string {
  return JSON.stringify({ info: info ?? null, teeth: teethState ?? [] });
}

export function AssessmentProvider() {
  const localTeeth = teethData as LocalTeethType[];

  const { activeOrganization, doctor, isOwner } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [patient, setPatient] = useState<Patient>();
  const [responsible, setResponsible] = useState<User>();
  const [info, setInfo] = useState<AssessmentInfo>();
  const [photos, setPhotos] = useState<FileResponse[]>();
  const [presentationEnabled, setPresentationEnabled] = useState(true);
  const [teeth, setTeeth] = useState<TeethType[] | undefined>(localTeeth.map(tooth => ({
    id: tooth.id[0],
    status: tooth.status.toLowerCase(),
    description: tooth.description || ""
  })));
  const [previewImage, setPreviewImage] = useState<{
    image: string;
    title: string;
    photoKey: string;
  }>();
  const [editImage, setEditImage] = useState<{
    image: string;
    title: string;
    photoKey: string;
  }>();

  

  const [assessmentId, setAssessmentId] = useState<string>('');
  const [allowAutoPersist, setAllowAutoPersist] = useState(true);
  const [isNewReady, setIsNewReady] = useState(false);
  const isInitialLoad = useRef(true);
  /** Último estado de info+teeth já salvo ou hidratado do servidor (não dispara persist se igual). */
  const lastAutoPersistSnapshotRef = useRef<string | null>(null);
  const isLoadingAssessment = useRef(false);

  const createAssessmentMutation = useCreateAssessment();
  const { showToast } = useToast();
  const createAssessment = async (assessmentInfo: AssessmentInfo) => {
    const resolvedUserId = isOwner ? (responsible?.id || '') : (doctor?.id || responsible?.id || '');
    const { data } = await createAssessmentMutation.mutateAsync({
      status: assessmentInfo.status || 'INITIAL',
      organizationId: activeOrganization?.id || '',
      patientId: patient?.id || '',
      userId: resolvedUserId,
      description: assessmentInfo.description,
      category: assessmentInfo.category,
      obs: assessmentInfo.obs,
      presentationEnabled: true,
    });

    setAssessmentId(data.id);
    setPresentationEnabled(data.presentationEnabled ?? true);
    return data;
  }

  const updateAssessment = async () => {
    if (!assessmentId) {
      throw new CustomError('Assessment ID não encontrado', 'ASSESSMENT_ID_NOT_FOUND', 400);
    }

    await updateAssessmentService(assessmentId, {
      status: info?.status || 'INITIAL',
      description: info?.description,
      category: info?.category,
      obs: info?.obs,
      teeth: teeth || [],
    });
  }

  const loadAssessment = async (id: string): Promise<void> => {
    try {
      // Marca que está carregando
      isLoadingAssessment.current = true;
      
      // Marca que está no processo de carregamento inicial
      isInitialLoad.current = true;
      lastAutoPersistSnapshotRef.current = null;

      const { data: assessment } = await getAssessment(id);

      setAssessmentId(id);
      
      if (assessment?.patient) {
        setPatient(assessment.patient as Patient);
      }
      if (assessment?.doctor) {
        setResponsible(assessment.doctor as User);
      }
      
      const newInfo: AssessmentInfo = {
        description: assessment?.description || "",
        status: assessment?.status || 'INITIAL',
        category: assessment?.category || "",
        obs: assessment?.obs || "",
      };
      
      const loadedTeeth = assessment.teeth as TeethType[];
      setInfo(newInfo);
      setPhotos(assessment.files as FileResponse[]);
      setPresentationEnabled(assessment.presentationEnabled ?? true);
      setTeeth(loadedTeeth);
      lastAutoPersistSnapshotRef.current = autoPersistSnapshot(newInfo, loadedTeeth);
      setLoaded(true);

      isInitialLoad.current = false;
    } catch (error) {
      console.error('Erro ao carregar assessment:', error);
      throw new CustomError('Erro ao carregar assessment', 'ASSESSMENT_LOAD_ERROR', 400);
    } finally {
      // Libera o flag de carregamento
      isLoadingAssessment.current = false;
    }
  };

  const uploadImage = async (originalFile: File, key: string, editedFile?: File) => {
    if (!assessmentId) {
      throw new CustomError('Assessment ID não encontrado', 'ASSESSMENT_ID_NOT_FOUND', 400);
    }

    try {
      const existing = photos?.find((p) => p.key === key)?.value ?? [];
      const existingOriginalStorageKey = existing.find((v) => v.original)?.file.key || "";
      const existingEditedStorageKey = existing.find((v) => !v.original)?.file.key || "";
      const existingOriginalFileName = existingOriginalStorageKey.replace(/^assessment\//, "");
      const existingEditedFileName = existingEditedStorageKey.replace(/^assessment\//, "");

      // Gera hash único para o arquivo
      const hash = Math.random().toString(36).substr(2, 9);

      // Regras:
      // - Se vem do PreviewImage (sem editedFile), é upload/substituição de "original": sempre envia original.
      // - Se vem do EditImage (com editedFile), envia original só se ainda não existir original nessa chave.
      const shouldUploadOriginal = !editedFile || !existingOriginalStorageKey;
      const shouldUploadEdited = Boolean(editedFile);

      const files: Array<{ file: string; original: boolean }> = [];

      let originalFileName: string | null = null;
      let editedFileName: string | null = null;

      if (shouldUploadOriginal) {
        originalFileName = `${key}-original-${hash}.jpg`;
        await uploadFile(originalFile, 'assessment', originalFileName);
        files.push({ file: originalFileName, original: true });
      } else if (existingOriginalFileName) {
        // mantém referência atual da original no banco (sem reupload)
        files.push({ file: existingOriginalFileName, original: true });
      }

      // Se há imagem editada, faz upload dela também
      if (shouldUploadEdited && editedFile) {
        editedFileName = `${key}-edited-${hash}.jpg`;
        await uploadFile(editedFile, 'assessment', editedFileName);
        files.push({ file: editedFileName, original: false });
      } else if (!shouldUploadEdited && existingEditedFileName) {
        // mantém referência atual da editada
        files.push({ file: existingEditedFileName, original: false });
      }

      // Atualiza os arquivos no assessment
      await updateAssessmentFiles(assessmentId, {
        key,
        value: files
      });

      // Remove versões antigas do R2 quando foram substituídas
      const keysToDelete: string[] = [];

      // Substituição de original: remove original e editada antigas
      if (shouldUploadOriginal) {
        if (existingOriginalStorageKey) keysToDelete.push(existingOriginalStorageKey);
        if (existingEditedStorageKey) keysToDelete.push(existingEditedStorageKey);
      } else if (shouldUploadEdited) {
        // Edição: remove apenas a editada antiga
        if (existingEditedStorageKey) keysToDelete.push(existingEditedStorageKey);
      }

      if (keysToDelete.length > 0) {
        void deleteFiles({ keys: Array.from(new Set(keysToDelete)) }).catch(() => {});
      }

      // Recarrega o assessment para atualizar a lista de fotos com dados frescos do servidor
      const { data: assessment } = await getAssessment(assessmentId);
      if (assessment?.files) {
        setPhotos(assessment.files as FileResponse[]);
      }

      // Não fecha aqui - deixa EditImage/PreviewImage fechar após mostrar o toast
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      throw new CustomError(getErrorMessage(error, 'Erro ao salvar imagem'), 'UPLOAD_ERROR', 500);
    }
  };

  const resetFiles = async (key: string) => {
    const existing = photos?.find((p) => p.key === key)?.value ?? [];
    const keysToDelete = existing.map((v) => v.file.key).filter(Boolean);

    // Evita preview/edição com URL antiga enquanto o estado ainda não atualizou
    setPreviewImage((prev) => (prev?.photoKey === key ? undefined : prev));
    setEditImage((prev) => (prev?.photoKey === key ? undefined : prev));

    // Atualização otimista: remove o slot já na UI (um clique = feedback imediato)
    setPhotos((prev) => (prev ? prev.filter((p) => p.key !== key) : prev));

    try {
      await updateAssessmentFiles(assessmentId, {
        key,
        value: [],
      });

      if (keysToDelete.length > 0) {
        void deleteFiles({ keys: Array.from(new Set(keysToDelete)) }).catch(() => {});
      }

      // Mesmo fluxo do upload: fonte da verdade é o GET após o PATCH
      const { data: assessment } = await getAssessment(assessmentId);
      if (assessment?.files?.length) {
        setPhotos(assessment.files as FileResponse[]);
      } else {
        setPhotos(undefined);
      }
    } catch (e) {
      const { data: assessment } = await getAssessment(assessmentId);
      if (assessment?.files?.length) {
        setPhotos(assessment.files as FileResponse[]);
      } else {
        setPhotos(undefined);
      }
      throw e;
    }
  };

  const reset = () => {
    setPatient(undefined);
    setResponsible(undefined);
    setInfo(undefined);
    setPhotos(undefined);
    setPresentationEnabled(true);
    setTeeth(undefined);
    setAssessmentId('');
    setAllowAutoPersist(true);
    setLoaded(false);
    setIsNewReady(false);
    isInitialLoad.current = true;
    lastAutoPersistSnapshotRef.current = null;
    isLoadingAssessment.current = false;
  }

  // Em novo atendimento: considera pronto após o ciclo de montagem da página
  useEffect(() => {
    if (!assessmentId) {
      const t = requestAnimationFrame(() => setIsNewReady(true));
      return () => cancelAnimationFrame(t);
    }
    setIsNewReady(false);
  }, [assessmentId]);

  const isAssessmentReady = assessmentId ? loaded : isNewReady;

  useEffect(() => {
    if (!loaded || !assessmentId || isInitialLoad.current || !allowAutoPersist) return;

    const snapshot = autoPersistSnapshot(info, teeth);
    if (snapshot === lastAutoPersistSnapshotRef.current) return;

    let cancelled = false;
    void (async () => {
      try {
        await updateAssessment();
        if (cancelled) return;
        lastAutoPersistSnapshotRef.current = snapshot;
        showToast({
          title: 'Sucesso!',
          message: 'Avaliação atualizada com sucesso',
          position: 'top-right',
          type: 'success',
        });
      } catch (e) {
        console.error('Erro ao persistir avaliação automaticamente:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [info, teeth, loaded, assessmentId, allowAutoPersist]);

  return (
    <AssessmentContext.Provider value={{
        patient,
        setPatient,
        responsible,
        setResponsible,
        info,
        setInfo,
        photos,
        setPhotos,
        presentationEnabled,
        setPresentationEnabled,
        teeth,
        setTeeth,
        previewImage,
        setPreviewImage,
        editImage,
        setEditImage,
        createAssessment,
        loadAssessment,
        updateAssessment,
        uploadImage,
        resetFiles,
        reset,
        isAssessmentReady,
        allowAutoPersist,
        setAllowAutoPersist,
    }}>
      <Outlet />
      {editImage &&
        <EditImage />
      }
      {previewImage &&
        <PreviewImage />
      }
    </AssessmentContext.Provider>
  );
}
